import { Component, OnInit, OnDestroy } from "@angular/core";
// providers
import { SharedService } from "../shared/services/shared.service";
import { AuthService } from "../shared/services/auth.service";
// redux
import { Subject, Subscription } from "rxjs";
import { BroshService } from "../shared/services/brosh.service";
import { DataService } from "../shared/services/data.service";
import { ChatService } from "../shared/services/chat.service";
import { UsersService } from "../shared/services/users.service";
import { Howl, Howler } from "howler";
import { PushNotificationsService } from "../shared/services/push-notifications.service";
import { Store } from "@ngrx/store";
import {
  ui_mobileNavOpen,
  userNotifications,
} from "../shared/ngrx/ngrx-selectors";
import {
  GET_USER_NOTIFICATIONS,
  REQUEST_DASHBOARD_REFRESH,
  UI__SET_LANG,
} from "../shared/ngrx/ngrx-actions";
import { map, takeUntil } from "rxjs/operators";
import { ChatTask } from "../shared/models/chat.model";
import { Actions, ofType } from "@ngrx/effects";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { SidebarComponent } from "./sidebar/sidebar.component";
import { NavbarComponent } from "./navbar/navbar.component";
import { PopupComponent } from "../shared/components/ui/popup.component";
import { ReportsService } from "../shared/services/reports.service";
import { UsersSearchParams } from "../shared/models/ui-user.model";

@Component({
  selector: "app-layout",
  templateUrl: "./layout.component.html",

  imports: [
    CommonModule,
    RouterModule,
    SidebarComponent,
    NavbarComponent,
    PopupComponent,
  ],
  standalone: true,
})
export class LayoutComponent implements OnInit, OnDestroy {
  generalLoader: boolean;
  isSidebarOpen: boolean = false;
  getUserNotificationsTimer: any;
  getUserNotificationsIntervalSeconds: number = 60 * 1;
  firstTimeLoad: boolean = true;
  initialLoaderRemoved: boolean = false;
  destroy$ = new Subject();
  isMobileNavOpen$ = this.store.select(ui_mobileNavOpen);

  sound = new Howl({
    src: ["assets/bell.mp3"],
    loop: false,
    volume: 0.4,
  });
  contact: any;

  constructor(
    private readonly store: Store,
    private sharedService: SharedService,
    private dataService: DataService,
    private broshService: BroshService,
    private authService: AuthService,
    private chatService: ChatService,
    private notificationService: PushNotificationsService,
    private actions$: Actions,
    private reportsService: ReportsService,
    private usersService: UsersService
  ) {
    this.notificationService.requestPermission();
  }

  ngOnInit() {
    this.generalLoader = false;

    this.sharedService.isSidebarOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.isSidebarOpen = data;
      });

    this.loadInitialData();

    this.actions$
      .pipe(
        ofType(REQUEST_DASHBOARD_REFRESH),
        takeUntil(this.destroy$),
        map(({ skipNotifications }) => {
          if (!skipNotifications) this.getUserNotifications();
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(UI__SET_LANG),
        takeUntil(this.destroy$),
        map(({ }) => {
          this.getStagesList();
          this.getUrgencyList();
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.clearNotificationsTimer();
    this.destroy$.next(1);
    this.destroy$.complete();
    this.sound.unload();
  }

  ngAfterViewChecked() {
    // remove initial loader (once — this hook runs on every CD cycle)
    if (this.initialLoaderRemoved) return;
    const initialLoadingDiv = document.getElementById("initial_loading");
    initialLoadingDiv &&
      initialLoadingDiv.parentNode.removeChild(initialLoadingDiv);
    this.initialLoaderRemoved = true;
  }

  loadInitialData() {
    this.getClinicList();
    this.getDoctorList();
    this.getStagesList();
    this.getSupplyTypesList();
    this.getClinicChainList();
    this.scheduleGetUserNotifications();
    this.getRoleList();
    this.getUrgencyList();
    this.getAgentsList();
    this.getTaskStatusList();
    this.getRXOpt();
    this.getRXItems();
    this.getTaskUsersList();
    this.getTechIssuesList();
    this.getTechniciansList();
    this.getKPIConfig();
    this.getDeliverersList();
  }

  getDeliverersList() {
    if (this.authService.currentUser.authDetails.getBooleanValue(
      "ORDERS.DELIVERER_FILTER_ENABLED"
    )) {
      this.dataService.getDeliverersList().subscribe();
    }
  }

  getTechIssuesList() {
    if (
      this.authService.currentUser.authDetails.getBooleanValue(
        "USERS_SCR.ALLOWED_SW"
      )
    ) {
      this.dataService.getTechIssuesList().subscribe();
    }
  }

  getTechniciansList() {
    if (
      this.authService.currentUser.authDetails.getBooleanValue(
        "USERS_SCR.ALLOWED_SW"
      )
    ) {
      this.dataService.getTechniciansList().subscribe();
    }
  }

  getAgentsList() {
    this.dataService.getAgentsList().subscribe();
  }

  getTaskStatusList() {
    this.dataService.getTaskStatusList().subscribe();
  }

  getKPIConfig() {
    this.reportsService.getKPIConfig().subscribe();
  }

  getClinicList() {
    this.dataService.getClinicList().subscribe();

    if (this.authService.currentUser.roleCode == "AGENT") {
      this.dataService
        .getClinicList(true, this.authService.currentUser.agentCode)
        .subscribe();
    }
  }

  getClinicChainList() {
    if (
      this.authService.currentUser.authDetails.getBooleanValue(
        "LAB_KPI.ALLOWED_SW"
      )
    ) {
      this.dataService.getClinicChainList().subscribe();
    }
  }

  getDoctorList() {
    this.dataService.getDoctorList().subscribe();
  }

  getStagesList() {
    this.dataService.getStageList().subscribe();
  }

  getUrgencyList() {
    this.dataService.getUrgencyOptions().subscribe();
  }

  getSupplyTypesList() {
    this.dataService.getSuppliesTypes().subscribe();
  }

  getRoleList() {
    this.dataService.getRoleList().subscribe();
  }

  getRXOpt() {
    this.dataService.getRXOpt().subscribe();
  }

  getRXItems() {
    this.broshService.getRXItems().subscribe();
  }

  getTaskUsersList() {
    let userParams = new UsersSearchParams();
    userParams.rangeFrom = 0;
    userParams.rangeTo = 100;
    userParams.taskUsersSw = true;

    if (this.authService.currentUser.org == "LAB") {
      userParams.org = "LAB";
    } else if (this.authService.currentUser.org == "PLANT") {
      userParams.org = "PLANT";
      userParams.plant = this.authService.currentUser.plant;
    }
    this.usersService
      .getUsers(userParams, false, true, true)
      .subscribe();
  }

  async getUserNotifications() {
    this.chatService.getUserNotifications().subscribe((data) => {
      let newUnread = data.filter(
        (n) =>
          !n.readSw &&
          !this.store
            .selectSignal(userNotifications)()
            .find((n2) => n2.id == n.id)
      );
      if (newUnread.length > 0) {
        this.sound.play();

        if (!this.firstTimeLoad) {
          this.notificationService.generateNotification(
            newUnread.map((n) => {
              return {
                title: "Brosh Online",
                alertContent: n.text,
                silent: true,
              };
            })
          );

          this.store.dispatch(REQUEST_DASHBOARD_REFRESH(true));
        }

        this.firstTimeLoad = false;
      }

      this.store.dispatch(
        GET_USER_NOTIFICATIONS({
          data: data,
        })
      );
    });
  }

  scheduleGetUserNotifications() {
    this.clearNotificationsTimer();
    this.getUserNotifications();

    this.getUserNotificationsTimer = setInterval(() => {
      this.getUserNotifications();
    }, this.getUserNotificationsIntervalSeconds * 1000);
  }

  clearNotificationsTimer() {
    if (this.getUserNotificationsTimer) {
      clearInterval(this.getUserNotificationsTimer);
    }
  }
}
